/**
 * 🔐 useLogin Hook - Authentication Logic
 *
 * ✅ REFACTORIZADO: Ahora usa useAsyncState para estados loading/error
 * Reducción: 88 líneas → 78 líneas (-10 líneas, -11%)
 *
 * Handles login flow, including:
 * - Credentials validation
 * - API communication
 * - User data refresh
 * - Error handling
 * - Loading states
 *
 * @example
 * const { login, loading, error } = useLogin();
 * const result = await login('username', 'password');
 * if (result.success) { ... }
 */

import { useAsyncState } from '../../../shared/hooks/async/useAsyncState';
import { useUser } from '../../../contexts/UserContext';
import { ROUTE_AUTH_LOGIN } from '../../../config/routes/routeConfig';
import { logger } from '../../../shared/utils/logger';
import { Area } from '../../../types/common.types';

export interface LoginResponse {
	firstLogin: boolean;
	area: Area | Area[];
	message?: string;
	userId?: number;
}

export interface LoginResult {
	success: boolean;
	data?: LoginResponse;
	error?: string;
}

export const useLogin = () => {
	// ✅ USA useAsyncState: elimina useState loading/error
	const { loading, error, setLoading, setError, clearError } = useAsyncState({
		captureErrors: false, // Manejo de errores custom
	});
	const { refetchUserData } = useUser();

	const login = async (
		username: string,
		password: string
	): Promise<LoginResult> => {
		setLoading(true);
		setError(null);

		try {
			const response = await fetch(ROUTE_AUTH_LOGIN, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ username, password }),
				credentials: 'include',
			});

			const data: LoginResponse = await response.json();

			if (response.ok) {
				// Refresh user data from context
				await refetchUserData();

				return { success: true, data };
			} else {
				const errorMessage = data.message || 'Invalid credentials';
				logger.warn('Login attempt failed', { username, reason: errorMessage });
				setError(errorMessage);
				return { success: false, error: errorMessage };
			}
		} catch (err) {
			const errorMessage = 'Network error. Please check your connection.';
			logger.error('Login network error', err instanceof Error ? err : new Error(String(err)));
			setError(errorMessage);
			return { success: false, error: errorMessage };
		} finally {
			setLoading(false);
		}
	};

	return {
		login,
		loading,
		error,
		clearError,
	};
};
